import React, { useCallback, useEffect, useRef } from 'react';

import { Repeat as RepeatIcon } from '@styled-icons/boxicons-regular/Repeat';
import { Download as DownloadIcon } from '@styled-icons/evaicons-solid/Download';
import { toast } from 'shared/toast';

import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  SimpleGrid,
  ListItem,
  UnorderedList,
  useMediaQuery,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import useAlert from 'hooks/useAlert';

import CompactCallout from './CompactCallout';
import InconsistenciesTable from './InconsistenciesTable';
import useBudgetConversionToSienge from './useBudgetConversionToSienge';

type BudgetConvertToSiengeProps = ModalRootProps & {
  data: {
    budgetId: number;
    shouldOpenConvertedBudgetInNewTab?: boolean;
    shouldWaitForIdentificationBeforeSuccess?: boolean;
  };
};

export const featureFlags = {
  enableExportInconsistenciesReport: true,
};

const modalMargin = 120;
const modalMobileFix = 100;

const ModalBudgetConversionToSienge: React.FC<BudgetConvertToSiengeProps> = ({
  data,
  handleClose,
  onConfirm,
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');
  const { openAlert } = useAlert();
  const modalBodyRef = useRef<HTMLDivElement | null>(null);

  const {
    status,
    inconsistencies,
    page,
    lastPage,
    loadErrorMessage,
    conversionErrorMessage,
    isExporting,
    isConverting,
    setPage,
    reload,
    requestExport,
    requestConversion,
  } = useBudgetConversionToSienge({
    budgetId: data.budgetId,
  });

  const handleAttemptClose = useCallback((): void => {
    if (isConverting) {
      return;
    }

    handleClose();
  }, [handleClose, isConverting]);

  const handleExport = useCallback(async (): Promise<void> => {
    const loadingToastId = toast({
      status: 'info',
      description: 'Gerando arquivo de inconsistências...',
      isClosable: false,
      duration: null,
    });

    const result = await requestExport();

    if (loadingToastId) {
      toast.close(loadingToastId);
    }

    if (!result.ok) {
      toast({
        status: 'error',
        description: result.errorMessage,
      });
      return;
    }

    toast({
      status: 'success',
      description: `Relatório solicitado com sucesso. O arquivo será baixado automaticamente quando estiver pronto.`,
    });
  }, [requestExport]);

  const handleConvert = useCallback(async (): Promise<void> => {
    const processingToastId = toast({
      status: 'info',
      description: `Convertendo orçamento para o padrão Sienge. Isso pode levar alguns instantes.`,
      isClosable: false,
      duration: null,
    });

    const result = await requestConversion({
      waitForIdentification: data.shouldWaitForIdentificationBeforeSuccess,
    });

    if (processingToastId) {
      toast.close(processingToastId);
    }

    if (!result.ok) {
      return;
    }

    const shouldOpenConvertedBudgetInNewTab =
      data.shouldOpenConvertedBudgetInNewTab !== false;
    const convertedBudgetUrl = `/budget/${result.duplicatedBudgetId}?active_tab=planning`;

    if (shouldOpenConvertedBudgetInNewTab) {
      const openedWindow = window.open(convertedBudgetUrl, '_blank');

      if (openedWindow) {
        toast({
          status: 'success',
          description: `Orçamento convertido com sucesso. Abrindo orçamento convertido...`,
        });
      } else {
        openAlert({
          type: 'info',
          size: '2xl',
          title: 'Orçamento convertido com sucesso!',
          buttons: ['Fechar', 'Finalizar conversão'],
          description: () => (
            <Text>
              Seu orçamento foi{' '}
              <Text as="span" fontWeight="bold">
                convertido com sucesso
              </Text>{' '}
              e está pronto para ser integrado com o{' '}
              <Text as="span" fontWeight="bold">
                Sienge Plataforma
              </Text>
              .{' '}
              <Link
                href={convertedBudgetUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="blue.500"
                fontWeight="bold"
                textDecoration="underline"
                display="inline-flex"
                alignItems="center"
                gap={1}
              >
                Clique aqui para abrir o orçamento convertido
                <ExternalLinkIcon aria-hidden />
              </Link>
            </Text>
          ),
        });
      }
    } else {
      toast({
        status: 'success',
        description: 'Conversão do orçamento solicitada com sucesso!',
      });
    }

    if (onConfirm) {
      onConfirm();
    }

    handleClose();
  }, [
    data.shouldOpenConvertedBudgetInNewTab,
    data.shouldWaitForIdentificationBeforeSuccess,
    handleClose,
    onConfirm,
    requestConversion,
    openAlert,
  ]);

  const hasInconsistencies = inconsistencies.length > 0;

  const handleContinueConversion = useCallback(() => {
    if (isConverting || status === 'loading' || status === 'error') {
      return;
    }

    openAlert({
      type: 'warning',
      size: '2xl',
      title: 'Confirmar conversão do orçamento?',
      description: () => (
        <Box>
          <Text mb={2}>
            Será criado um novo orçamento compatível com o padrão Sienge.
          </Text>
          <Text mb={2}>
            {hasInconsistencies
              ? 'O orçamento original permanecerá inalterado, porém alguns itens incompatíveis poderão ser removidos automaticamente do orçamento convertido.'
              : 'Esse processo pode demorar um pouco.'}
          </Text>
          <Text>Deseja continuar?</Text>
        </Box>
      ),
      buttons: ['Voltar', 'Confirmar conversão'],
      onConfirm: handleConvert,
    });
  }, [handleConvert, hasInconsistencies, isConverting, openAlert, status]);

  const shouldShowCompatibilitySuccess =
    status === 'empty' || (status === 'success' && !hasInconsistencies);
  const shouldShowLoadingState = status === 'loading';
  const shouldShowSuccessState =
    shouldShowCompatibilitySuccess && !conversionErrorMessage && !isConverting;
  const shouldUseExpandedLayout = status === 'success' && hasInconsistencies;
  const isPrimaryActionDisabled =
    isConverting || status === 'loading' || status === 'error';

  useEffect(() => {
    if (!isConverting || !modalBodyRef.current) {
      return;
    }

    if (typeof modalBodyRef.current.scrollTo === 'function') {
      modalBodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    modalBodyRef.current.scrollTop = 0;
  }, [isConverting]);

  return (
    <Modal
      {...restProps}
      size="4xl"
      scrollBehavior="inside"
      closeOnEsc={!isConverting}
      closeOnOverlayClick={!isConverting}
      onClose={handleAttemptClose}
    >
      <ModalOverlay />

      <ModalContent
        sx={
          shouldUseExpandedLayout
            ? {
                width: `calc(100% - ${
                  isMobile ? modalMargin - modalMobileFix : modalMargin
                }px)`,
                maxWidth: `calc(100% - ${
                  isMobile ? modalMargin - modalMobileFix : modalMargin
                }px)`,
                height: `calc(100% - ${
                  isMobile ? modalMargin - modalMobileFix : modalMargin
                }px)`,
                maxHeight: `calc(100% - ${
                  isMobile ? modalMargin - modalMobileFix : modalMargin
                }px)`,
              }
            : undefined
        }
      >
        <ModalHeader>Converter para orçamento tipo Sienge</ModalHeader>
        <ModalCloseButton isDisabled={isConverting} />

        <ModalBody overflowY="auto" ref={modalBodyRef}>
          <CompactCallout
            variant="info"
            title="Avisos importantes sobre a conversão"
            mb={4}
            testId="conversion-notes-callout"
          >
            <UnorderedList ml={4} spacing={1.5}>
              <ListItem>
                O{' '}
                <Text as="span" fontWeight="semibold">
                  orçamento original
                </Text>{' '}
                não será alterado
              </ListItem>
              <ListItem>
                Um{' '}
                <Text as="span" fontWeight="semibold">
                  novo orçamento
                </Text>{' '}
                será criado com estrutura compatível com o Sienge Plataforma
              </ListItem>
              <ListItem>
                O arredondamento de cálculos do orçamento Sienge{' '}
                <Text as="span" fontWeight="semibold">
                  pode gerar variações de valores
                </Text>
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="semibold">
                  Estruturas não suportadas pelo orçamento Sienge poderão ser
                  removidas
                </Text>{' '}
                automaticamente
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="semibold">
                  Revisão pós-conversão:
                </Text>{' '}
                recomendamos revisar os valores utilizando alteração global
              </ListItem>
            </UnorderedList>
          </CompactCallout>

          {shouldShowLoadingState && (
            <CompactCallout
              variant="info"
              title="Validando inconsistências do orçamento"
              testId="conversion-loading-callout"
            >
              <Flex alignItems="center" gap={2}>
                <Spinner size="sm" color="blue.500" />
                <Text>
                  Carregando itens e validando compatibilidade com o padrão
                  Sienge.
                </Text>
              </Flex>
            </CompactCallout>
          )}

          {shouldShowSuccessState && (
            <CompactCallout
              variant="success"
              title="Orçamento pronto para conversão"
              testId="conversion-ready-callout"
            >
              <Text>
                Nenhuma inconsistência estrutural foi identificada para o padrão
                Sienge.
              </Text>
            </CompactCallout>
          )}

          {status === 'success' && hasInconsistencies && (
            <CompactCallout
              variant="warning"
              title="Inconsistências detectadas"
              mb={4}
              testId="conversion-inconsistencies-callout"
            >
              <Text mb={3}>
                Esses itens serão removidos e não estarão presentes no orçamento
                convertido.
              </Text>

              <Box
                borderWidth="1px"
                borderColor="blackAlpha.100"
                borderRadius="8px"
                bg="whiteAlpha.700"
              >
                <SimpleGrid columns={{ base: 1, md: 2 }} bg="blackAlpha.50">
                  <Box px={3} py={2}>
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      Itens impactados
                    </Text>
                  </Box>
                  <Box
                    px={3}
                    py={2}
                    borderTopWidth={{ base: '1px', md: 0 }}
                    borderLeftWidth={{ base: 0, md: '1px' }}
                    borderColor="blackAlpha.100"
                  >
                    <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                      Correções sugeridas
                    </Text>
                  </Box>
                </SimpleGrid>

                {[
                  {
                    impacted: 'Insumos adicionados diretamente na EAP',
                    suggestion: `Transforme insumos avulsos em serviços próprios`,
                  },
                  {
                    impacted: 'Serviços com Preço Informado',
                    suggestion: `Substitua serviços com preço informado por serviços próprios do tipo Sienge com preço informado por Grupo de insumo`,
                  },
                  {
                    impacted: `Itens com níveis que excedam o limite do Sienge Plataforma`,
                    suggestion: `Reduza o aninhamento do orçamento para até 3 níveis + serviços`,
                  },
                ].map((row) => (
                  <SimpleGrid
                    key={row.impacted}
                    columns={{ base: 1, md: 2 }}
                    borderTopWidth="1px"
                    borderColor="blackAlpha.100"
                  >
                    <Box px={3} py={2.5}>
                      <Text fontSize="sm">{row.impacted}</Text>
                    </Box>
                    <Box
                      px={3}
                      py={2.5}
                      borderTopWidth={{ base: '1px', md: 0 }}
                      borderLeftWidth={{ base: 0, md: '1px' }}
                      borderColor="blackAlpha.100"
                    >
                      <Text fontSize="sm">{row.suggestion}</Text>
                    </Box>
                  </SimpleGrid>
                ))}
              </Box>
            </CompactCallout>
          )}

          {hasInconsistencies &&
            featureFlags.enableExportInconsistenciesReport && (
              <>
                <Divider my={4} />

                <Flex justifyContent="space-between" alignItems="center" mb={3}>
                  <Box />

                  <Button
                    colorScheme="siengeBlue"
                    leftIcon={<Icon as={DownloadIcon} />}
                    onClick={handleExport}
                    isLoading={isExporting}
                    isDisabled={isConverting || status === 'loading'}
                  >
                    Exportar inconsistências
                  </Button>
                </Flex>
              </>
            )}

          {status === 'error' && (
            <CompactCallout
              variant="error"
              title="Não foi possível carregar as inconsistências"
              mb={3}
              testId="conversion-load-error-callout"
              action={
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Icon as={RepeatIcon} />}
                  onClick={reload}
                  isDisabled={isConverting}
                >
                  Tentar novamente
                </Button>
              }
            >
              <Text>
                {loadErrorMessage ||
                  'Tente novamente para validar o orçamento antes da conversão.'}
              </Text>
            </CompactCallout>
          )}

          {conversionErrorMessage && (
            <CompactCallout
              variant="error"
              title="Falha ao converter o orçamento"
              mb={3}
              testId="conversion-error-callout"
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleContinueConversion}
                  isDisabled={isConverting}
                >
                  Tentar novamente
                </Button>
              }
            >
              <Text>{conversionErrorMessage}</Text>
            </CompactCallout>
          )}

          {isConverting && (
            <CompactCallout
              variant="info"
              title="Convertendo orçamento"
              mb={4}
              testId="conversion-processing-callout"
            >
              <Flex alignItems="center" gap={2}>
                <Spinner size="sm" color="blue.500" />
                <Text>
                  Convertendo orçamento... Isso pode levar alguns instantes.
                </Text>
              </Flex>
            </CompactCallout>
          )}

          {hasInconsistencies && (
            <>
              <InconsistenciesTable
                budgetId={data.budgetId}
                data={inconsistencies}
                loading={status === 'loading'}
                isMobile={isMobile}
                noDataMessage="Nenhuma inconsistência encontrada."
              />

              {status !== 'loading' && lastPage > 1 && (
                <PaginationWrapper
                  lastPage={lastPage}
                  forcePage={page - 1}
                  onPaginate={(nextPage) => {
                    setPage(nextPage);
                  }}
                />
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button
              variant="ghost"
              onClick={handleAttemptClose}
              isDisabled={isConverting}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              onClick={handleContinueConversion}
              isDisabled={isPrimaryActionDisabled}
              isLoading={isConverting}
            >
              Converter orçamento
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetConversionToSienge;
